import { DEPLOY_ENV } from './constants';
import { isOidcConfigured, userManager } from './oidc';
import { toCurrentTld } from './workDomain';

// video-sage has a .work mirror; keep the call on the user's current TLD so the
// browser doesn't hit the geo-blocked .ai host from the .work app.
const VIDEO_SAGE_BASE = toCurrentTld(
  DEPLOY_ENV === 'prod'
    ? 'https://video-sage.maxinsights.ai'
    : 'https://video-sage-staging.maxinsights.ai'
);

const VIDEO_SAGE_ANALYZE_URL = `${VIDEO_SAGE_BASE}/analyze`;
const VIDEO_SAGE_REVISE_URL = `${VIDEO_SAGE_BASE}/revise`;

export interface LlmAnalyzeResult {
  tasks: Array<{ en: string; zh: string }>;
  model: string;
  llmDurationS: number;
}

async function authHeader(): Promise<HeadersInit> {
  if (!isOidcConfigured) return {};
  const user = await userManager.getUser();
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
}

export async function analyzeAction(
  episodeId: string,
  videoBlob: Blob,
  startSecond: number,
  endSecond: number
): Promise<LlmAnalyzeResult> {
  const form = new FormData();
  form.append('episodeId', episodeId);
  form.append('start_time', String(startSecond));
  form.append('end_time', String(endSecond));
  form.append('video', videoBlob, 'clip.mp4');

  console.debug('[llmClient] sending clip', {
    episodeId,
    startSecond,
    endSecond,
    blobSizeMB: (videoBlob.size / 1024 / 1024).toFixed(2),
  });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120_000);

  let response: Response;
  try {
    response = await fetch(VIDEO_SAGE_ANALYZE_URL, {
      method: 'POST',
      headers: await authHeader(),
      body: form,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore parse failure
    }
    throw new Error(message);
  }

  return response.json() as Promise<LlmAnalyzeResult>;
}

export interface ReviseResult {
  en: string;
  zh: string;
  source: 'llm' | 'short_circuit';
}

export async function reviseText(
  episodeId: string,
  startSecond: number,
  endSecond: number,
  zh: string
): Promise<ReviseResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60_000);

  let response: Response;
  try {
    response = await fetch(VIDEO_SAGE_REVISE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify({ episodeId, start_time: startSecond, end_time: endSecond, zh }),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore parse failure
    }
    throw new Error(message);
  }

  const data = (await response.json()) as {
    en: string;
    zh: string;
    source: string;
    status: string;
    error?: string;
  };
  if (data.status !== 'ok') throw new Error(data.error ?? `Revise failed: ${data.status}`);
  return { en: data.en, zh: data.zh, source: data.source as ReviseResult['source'] };
}
